import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import duplexVsAduImage from '@/assets/duplex-vs-adu.jpg';

const DuplexVsADUDifferenceCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/duplex-vs-adu-difference-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={duplexVsAduImage} 
              alt="Duplex vs ADU comparison" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              What's the Difference Between a Duplex and an ADU in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Clarify the key differences between a duplex and an ADU in California, 
              including zoning, usage, and legal distinctions.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Definition and Characteristics: Duplex vs ADU
              </h2>
              <p className="text-white leading-relaxed mb-6">
                A duplex is a building divided into two separate living units, typically 
                built as a single structure with shared walls. An ADU (Accessory Dwelling Unit) 
                is a secondary housing unit on a single-family property, which can be attached, 
                detached, or converted from existing space. ADUs are considered accessory to 
                the main residence, while duplexes are primary residential structures.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Zoning Regulations and Differences
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Duplexes typically require multi-family zoning or specific duplex zoning approval. 
                ADUs can be built in single-family residential zones throughout California, 
                thanks to state legislation. This makes ADUs much more accessible for homeowners 
                in standard residential neighborhoods where duplexes would not be permitted.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Legal and Taxation Differences Explained
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Duplexes are generally treated as separate legal units and can be sold individually 
                more easily than ADUs. For taxation purposes, duplexes may qualify for different 
                assessment methods. ADUs require special procedures like AB 1033 condo conversion 
                to be sold separately, while duplex units typically have established separate 
                legal descriptions from construction.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: Are ADUs and duplexes treated differently by zoning laws?</p>
                  <p>A: Yes, duplexes typically require multi-family zoning; ADUs can be built in single-family zones.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Can duplexes be split and sold separately like ADUs?</p>
                  <p>A: Duplexes generally can be sold separately, but ADUs require special conditions (e.g., AB 1033).</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DuplexVsADUDifferenceCalifornia;